import {
  AfterViewInit,
  Component,
  Inject,
  OnInit,
  ViewChild
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import {
  CustomValidators,
  ErroMessages,
  IErroMessages,
  TokenService
} from 'viggo-sistemas-core-web';

import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { IVariacaoMateria } from '@app/modules/variacao-materias/model/variacao-materias.model';
import { VariacaoMateriaService } from '@app/modules/variacao-materias/services/variacao-materias.service';
import {
  IVariacao,
  IVariacaoItem
} from '@app/modules/variacao-produtos/model/variacao.model';
import {
  ConfirmDialogComponent,
  ConfirmDialogData
} from '@app/shared/components';
import { Utils } from '@app/shared/helpers/utils';
import { ScreenService } from '@app/shared/services';
import { Dialog } from 'primeng/dialog';
import { filter, map, mergeMap } from 'rxjs/operators';

@Component({
  selector: 'app-fast-variacao-materia',
  templateUrl: './fast-variacao-materia.component.html',
  styleUrls: ['./fast-variacao-materia.component.scss'],
  providers: [VariacaoMateriaService]
})
export class FastVariacaoMateriaComponent implements OnInit, AfterViewInit {
  @ViewChild('fastVariacaoMP') fastVariacaoMP: Dialog;
  form: UntypedFormGroup;
  itens: UntypedFormControl;

  loading = false;
  entity: IVariacaoMateria;

  public displayedColumns: string[] = ['index', 'descricao', 'delete'];

  displayMessages = {
    descricao: {
      repeated: 'Já existe uma variação com esse nome!',
      required: 'A descrição é obrigatória',
      maxlength: 'Não pode ter mais de 100 caracteres',
      isEmptyValue: 'Valor inválido: remova os espaços em branco!'
    },
    descricao_item: {
      required: 'A Variação é obrigatória',
      maxlength: 'Não pode ter mais de 100 caracteres',
      isEmptyValue: 'Valor inválido: remova os espaços em branco!'
    }
  };

  constructor(
    public screenService: ScreenService,
    public dialogRef: MatDialogRef<FastVariacaoMateriaComponent>,
    public matDialog: MatDialog,
    private formBuilder: UntypedFormBuilder,
    public entityService: VariacaoMateriaService,
    public tokenService: TokenService,
    public matSnackBar: MatSnackBar,
    public router: Router,
    @Inject(MAT_DIALOG_DATA) private data: { entity: IVariacaoMateria }
  ) {}

  ngAfterViewInit(): void {
    if (this.screenService.detectDeviceInfo().isMobile)
      this.fastVariacaoMP.maximize();
  }

  ngOnInit(): void {
    this.entity = this.data.entity;
    this.form = this.formBuilder.group({
      descricao: [
        this.entity.descricao,
        [
          Validators.required,
          Validators.maxLength(100),
          CustomValidators.isEmptyValue()
        ]
      ],
      descricao_item: [
        '',
        [Validators.maxLength(100), CustomValidators.isEmptyValue()]
      ]
    });

    this.itens = new UntypedFormControl(this.entity.itens);

    this.markAllControlsAsDirty();
  }

  enterAdd(event) {
    if (event.key === 'Enter') {
      this.addItem();
    }
  }

  addItem() {
    const { value: prevValue } = this.itens;
    const { descricao_item } = this.form.value;
    const index = this.itens.value.findIndex(
      p => p.descricao === descricao_item
    );
    if (index >= 0) {
      prevValue.splice(index, 1);
      this.itens.setValue([
        ...prevValue,
        { descricao: descricao_item, variacao_id: this.entity.id }
      ]);
      this.reloadIndex();
    } else {
      this.itens.setValue([
        ...prevValue,
        { descricao: descricao_item, variacao_id: this.entity.id }
      ]);
      this.reloadIndex();
    }
  }

  rmItem(event: IVariacaoItem) {
    this.matDialog
      .open(ConfirmDialogComponent, {
        data: {
          title: 'Remover variação',
          bodyMessage: `Deseja remover a variação Nº ${event.index} - ${event.descricao}?`,
          icon: 'delete',
          icon_color: 'warn'
        }
      })
      .afterClosed()
      .subscribe(res => {
        if (res) {
          const { value: prevValue } = this.itens;
          const index = this.itens.value.findIndex(
            p => p.index === event.index
          );
          if (index >= 0) {
            prevValue.splice(index, 1);
            this.itens.setValue(prevValue);
            this.reloadIndex();
          }
        }
      });
  }

  rmTodos() {
    this.matDialog
      .open(ConfirmDialogComponent, {
        data: {
          icon: 'delete',
          icon_color: 'warn',
          title: 'Remover variações',
          bodyMessage: `Deseja remover todas as variações adicionadas?`
        }
      })
      .afterClosed()
      .subscribe(res => {
        if (res) {
          this.itens.setValue([]);
        }
      });
  }

  reloadIndex(): void {
    const { value } = this.itens;
    this.itens.setValue(value.map((v, i) => ({ ...v, index: i + 1 })));
    this.form.controls.descricao_item.reset();
  }

  public submit(): void {
    if (!this.form.valid) return;

    this.loading = true;
    const { value } = this.form;
    // get values from entity and form value
    this.entity = {
      ...this.entity,
      domain_id: this.tokenService.getDomain().id,
      descricao: this.form.value.descricao,
      itens: this.itens.value
    };
    this.entity = Utils.trimAttributes(this.entity);
    this.entity.itens.forEach(it => {
      delete it.index;
    });

    this.entityService
      .insert(this.entity)
      .pipe(
        mergeMap(variacao => {
          return this.entityService.getEntity(variacao.id, 'itens');
        })
      )
      .subscribe(
        variacao => {
          if (variacao) {
            this.matSnackBar.open(`Variação cadastrada com sucesso.`, 'X', {
              duration: 2000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: ['msg-success']
            });
            this.dialogRef.close(variacao);
          }
        },
        (err: HttpErrorResponse) => {
          this.displayError(err);
          this.loading = false;
        },
        () => {
          this.loading = false;
        }
      );
  }

  markAllControlsAsDirty() {
    Object.keys(this.form.controls).forEach(controlKey => {
      controlKey = String(controlKey);
      const control = this.form.get(controlKey);
      if (!control || control.valid) return;
      control.markAsDirty();
    });
  }

  validateNumbers(event) {
    if (
      !/^\d+$/.test(event.key) &&
      !event.ctrlKey &&
      event.key !== 'Backspace' &&
      event.key !== 'Delete' &&
      event.key !== 'Tab' &&
      event.key !== 'ArrowLeft' &&
      event.key !== 'ArrowRight'
    )
      return false;
  }

  /**
   * Verifica se o nome do catalogo já foi cadastrado
   */
  public checarExisteNome(): void {
    const descricaoControl = this.form.controls.descricao;

    if (descricaoControl.invalid || descricaoControl.value === '') return;

    const descricao = descricaoControl.value;

    this.entityService
      .getEntitiesByParams({
        where: {
          descricao,
          domain_id: this.tokenService.getDomain().id,
          filtrar_descricao_ilike: true
        } as any,
        list_options: 'ACTIVE_AND_INACTIVE'
      })
      .pipe(
        map(variacoes => {
          if (!variacoes || !variacoes.length) {
            return {} as IVariacao;
          }
          return variacoes[0];
        }),
        filter(
          variacao => variacao && variacao.id && variacao.id !== this.entity.id
        )
      )
      .subscribe(
        variacao => {
          descricaoControl.setErrors({ repeated: true });
        },
        (err: HttpErrorResponse) => this.displayError(err)
      );
  }

  displayError(err: HttpErrorResponse, showDefaultMsg = false) {
    const errorMsg = new ErroMessages({
      route: this.router.url,
      router: this.router,
      matSnackBar: this.matSnackBar,
      httpError: err,
      showDefaultMsg: showDefaultMsg,
      loginService: null,
      tokenService: this.tokenService,
      dialogComponent: ConfirmDialogComponent,
      matDialog: this.matDialog,
      dialogData: {
        config: {
          data: {
            title: 'Aviso - Erro ao realizar operação',
            bodyMessage: err.error,
            icon: 'warning',
            icon_color: 'accent',
            singleBtn: true
          } as ConfirmDialogData,
          disableClose: true,
          width: '500px'
        } as MatDialogConfig<any>
      }
    } as IErroMessages);
    errorMsg.showMessageObject(err.error);
    // errorMsg.checkError();
  }
}
